import type { CbStatus } from './status';
import { fmtDate } from './format';

export interface StatusQuestion {
    title: string;
    message: string;
    confirmLabel: string;
    variant: 'danger' | 'warning';
}

/**
 * Whether a status change must be asked first, and how (design audit 2026-10-07, S1-04;
 * Roman: "Rückfrage").
 *
 * - **Lost** always: it writes the combination off, in a table Customer 360 shows too.
 * - **Open** only when something would be lost with it: `runStatusSet` clears the note on every
 *   state but InProgress, and the reminder on Open and Lost (api/.../shared/mutations.ts). A plain
 *   Open with nothing attached stays one click — asking then would teach people to click through.
 * - **Checked** clears the note too; it already has its own dialog, which says so
 *   (`ConsBusinessStatusModal`'s `noteToDrop`).
 *
 * `null` means: write it straight away. Checked and InProgress never come here; they open their
 * own dialog. One function for the list's menu and the drawer, so both ask the same question.
 */
export function statusChangeQuestion(
    next: CbStatus,
    productName: string | null,
    attached: { note: string | null | undefined; reviewDueAt: string | null | undefined },
): StatusQuestion | null {
    const what = productName ?? 'this combination';
    const drops: string[] = [];
    if (attached.note) drops.push('the note');
    if (attached.reviewDueAt) drops.push(`the reminder of ${fmtDate(attached.reviewDueAt)}`);
    const dropText = drops.length > 0 ? ` This also removes ${drops.join(' and ')}.` : '';

    if (next === 'Lost') {
        return {
            title: 'Mark as lost?',
            message: `${what} is written off for everybody, here and in Customer 360.${dropText}`,
            confirmLabel: 'Mark as lost',
            variant: 'danger',
        };
    }
    if (next === 'Open' && drops.length > 0) {
        return {
            title: 'Set back to Open?',
            message: `${what} goes back to Open.${dropText}`,
            confirmLabel: 'Set to Open',
            variant: 'warning',
        };
    }
    return null;
}
